import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { richTextListStyles } from "../../common/richTextListStyles"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

/* ------------------------------------------------------------------ */
/* Root — full-width section with vertical padding, column layout       */
/* ------------------------------------------------------------------ */
export const Root = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[48]}px;
  /* EGDE-339: drop the bottom padding — the application form that follows on the
     career page brings its own top padding, and the two stacked together left a
     large empty gap between the role-requirements list and the form. */
  padding: ${sizes.spacing[48]}px 0 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0 0;
  }
`

/* ------------------------------------------------------------------ */
/* Content wrapper — constrains article text to 800px on desktop        */
/* ------------------------------------------------------------------ */
export const ArticleContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[48]}px;

  @media (min-width: ${breakpoints.lg}px) {
    max-width: 800px;
  }
`

/* ------------------------------------------------------------------ */
/* Title + metadata container — pushed below image on mobile via order  */
/* ------------------------------------------------------------------ */
export const TitleMetaContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[24]}px;
  order: 2;

  @media (min-width: ${breakpoints.lg}px) {
    order: unset;
    max-width: 800px;
  }
`

export const JobTitle = styled(Typography)`
  color: ${colors.primitives.neutral600};
  width: 100%;
`

export const LocationTag = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[4]}px;
`

export const LocationLabel = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
  white-space: nowrap;
`

export const LocationValue = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const DateText = styled(Typography)`
  color: ${colors.primitives.neutral600};
`

export const MetadataDivider = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    width: 1px;
    align-self: stretch;
    background-color: ${colors.primitives.neutral300};
  }
`

/* ------------------------------------------------------------------ */
/* Social share icons row                                               */
/* ------------------------------------------------------------------ */
export const SocialShareRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[16]}px;

  @media (min-width: ${breakpoints.lg}px) {
    gap: 20px;
  }
`

export const ShareButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`

/* ------------------------------------------------------------------ */
/* Hero image                                                           */
/* ------------------------------------------------------------------ */
export const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 800 / 509;
  border-radius: ${sizes.borderRadius[16]}px;
  overflow: hidden;
  order: 1;

  @media (min-width: ${breakpoints.lg}px) {
    order: unset;
    max-width: 800px;
    border-radius: ${sizes.borderRadius[24]}px;
  }
`

/* EGDE-332: render desktop + mobile crops and toggle by breakpoint (was always
   desktop, so mobile showed the wrong crop). Mobile falls back to desktop. */
export const HeroImageDesktop = styled.div`
  position: absolute;
  inset: 0;
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
  }
`

export const HeroImageMobile = styled.div`
  position: absolute;
  inset: 0;
  display: block;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/* ------------------------------------------------------------------ */
/* Content sections (Job Description, Social Conditions, Requirements)  */
/* ------------------------------------------------------------------ */
export const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
`

export const SectionHeading = styled(Typography)`
  color: ${colors.primitives.neutral600};
  font-weight: 700 !important;
  width: 100%;
`

/* ------------------------------------------------------------------ */
/* Rich text wrapper — paragraph styling                                */
/* ------------------------------------------------------------------ */
export const RichTextWrapper = styled.div`
  width: 100%;
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  line-height: 20px;
  /* EGDE-533/534: the wrapper now stacks html/list segments (see
     RichTextSegments) — the column gap gives the rhythm the browser's default
     list margins used to provide between prose and bullet groups. */
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
  }

  & p {
    margin: 0;
  }

  /* EGDE-540: headings inherit the wrapper's body line-height (26px), so an
     H1's ~36px glyphs overflowed their line boxes and the section below
     painted over the descenders — "cut" text. Give headings a line box that
     fits their own font size. */
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    line-height: 1.2;
    margin: 0;
  }

  & strong,
  & b {
    font-weight: 700;
  }

  & a {
    /* EGDE-327: rich-text body hyperlinks render blue, not orange/green */
    /* !important overrides inline color:#... pasted into CMS rich text */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;

    &:hover {
      opacity: 0.7;
    }
  }

  /* EGDE-539: ordered lists stay in the html segments (RichTextSegments only
     converts <ul> to chevron rows) — style their decimal markers here, same
     treatment as every other rich-text body. */
  ${richTextListStyles}
`

/* ------------------------------------------------------------------ */
/* Bullet list section                                                  */
/* ------------------------------------------------------------------ */
export const BulletSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${sizes.spacing[16]}px;
`

export const BulletList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
`

/**
 * Each bullet item is a horizontal row: [icon, text].
 * RTL: source [icon, text] => visual [text LEFT, icon RIGHT].
 * Figma shows icon on RIGHT (inline-start), text expanding LEFT.
 */
export const BulletItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
`

/**
 * EGDE-354: align the chevron marker to the CENTER of the first text line so
 * multi-line (wrapping) bullet items keep the icon pinned to the first row.
 * The wrapper height matches the text line-height per breakpoint (20px mobile /
 * 26px desktop) and centers the icon within it — replacing the hardcoded
 * `padding-top: 4px` that only looked right for a single line-height.
 */
export const BulletIconWrapper = styled.div`
  flex-shrink: 0;
  width: 12px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: ${breakpoints.lg}px) {
    height: 26px;
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const BulletText = styled.span`
  flex: 1;
  color: ${colors.primitives.neutral600};
  text-align: start;
  font-size: 16px;
  line-height: 20px;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
  }

  & strong {
    font-weight: 700;
  }

  & a {
    /* EGDE-327: rich-text body hyperlinks render blue, not orange/green */
    /* !important overrides inline color:#... pasted into CMS rich text */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;

    &:hover {
      opacity: 0.7;
    }
  }
`

/* ------------------------------------------------------------------ */
/* Metadata on mobile                                                   */
/* ------------------------------------------------------------------ */
export const MetadataMobileStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[8]}px;
  width: 100%;

  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

/**
 * Mobile metadata row: [location, divider, date].
 * EGDE-51: align to the inline-start (right edge in RTL) so the row lines up
 * with the job title and breadcrumb above it — the design does NOT center this
 * row horizontally. align-items:center keeps the 1px divider vertically
 * centered between the location and date text.
 */
export const MetadataMobileRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.spacing[8]}px;
  width: 100%;
  justify-content: flex-start;
`

export const MetadataMobileDivider = styled.div`
  width: 1px;
  height: 16px;
  flex-shrink: 0;
  background-color: ${colors.primitives.neutral300};
`

export const MetadataDesktopRow = styled.div`
  display: none;

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
    align-items: center;
    gap: ${sizes.spacing[16]}px;
    width: 100%;
  }
`
